// Platform-default document requirement definitions per onboarding flow.
// Source of truth for `onboarding.document_requirements` rows with tenantId
// NULL. Rows are upsert-only — never hard-deleted — so downstream
// `documents.requirementId` FKs stay stable across reseeds.
//
// Lives in @repo/constants rather than @repo/db because the office invitation
// modals need the same catalog in the browser to offer pre-attachment, and a
// hand-copied second list is exactly what produced the duplicate-requirement
// bug in PET-708.
//
// Two independent axes, and keeping them independent is the point (PET-716):
//   - `isRequired` — required vs optional. The ONLY source of required-ness.
//   - `conditionKey` — a gating predicate; omitted means "applies on every
//     path". Evaluated per flow by `documentCondition` in the backend.
// Tenants override individual slugs with a tenant-scoped row on the same
// (flowKey, slug); these arrays are what a tenant inherits where it has none.

import type { DocumentRequirementDescriptor } from './document-requirement-sets'
import { FLEET_DRIVER_DOCUMENT_REQUIREMENTS } from './fleet-documents'
import type { OnboardingDocumentCondition, OnboardingRequirementScope } from './onboarding-flows'

// The agreement's own slot, filed only by the signing flow or the paper scan
// (`DOC-44`, `INS-61`); read by slug across the backend.
export const SIGNED_AGREEMENT_SLUG = 'signed_agreement'

export type OnboardingRequirementDefinition = {
  slug: string
  name: string
  description?: string
  isRequired: boolean
  oneOfGroup?: string
  conditionKey?: OnboardingDocumentCondition
  // Where `conditionKey` excludes the row, this still offers it as OPTIONAL
  // (PET-3913). Read off the code catalog by the resolver rather than stored:
  // `document_requirements_default_slug_open_uq` allows one open row per
  // (flow, slug), so "required on one path, optional on the other" cannot be two
  // rows, and the row's `is_required` is already spoken for.
  optionalConditionKey?: OnboardingDocumentCondition
  // Which entity the document hangs off. Omitted means `company`, which is what
  // every flow but `carrier_onboarding` uses — a third independent axis
  // alongside `isRequired` and `conditionKey` (PET-3167).
  scope?: OnboardingRequirementScope
  source: 'applicant_upload' | 'system_generated' | 'signing_flow'
  sortOrder: number
  maxFiles?: number
  acceptedMimeTypes?: string[]
}

// Shared across every flow that collects them, so the copy can't drift between
// the driver and carrier catalogs. Optional per the Petrafi matrix: we capture
// them for compliance but never block a submission on them.
const SUNBIZ_FILING: OnboardingRequirementDefinition = {
  slug: 'sunbiz_filing',
  name: 'Sunbiz filing',
  description: 'Florida corporate filing / equivalent state registration document.',
  isRequired: false,
  source: 'applicant_upload',
  sortOrder: 70,
}

const SAFER_SNAPSHOT: OnboardingRequirementDefinition = {
  slug: 'safer_snapshot',
  name: 'SAFER snapshot',
  description: 'FMCSA SAFER company snapshot.',
  isRequired: false,
  source: 'applicant_upload',
  sortOrder: 80,
}

// The pair that proves the operator is a registered business holding a DOT
// number. Optional to the applicant on every flow that collects them — Allied
// normally pulls both itself — but the office owes them all the same: `INS-41`
// blocks the hand-off to Emblem until they are on file and `OO-23` blocks
// approval for dispatch until they are approved.
//
// One list, here, because three places read it and a hand-copied second list is
// how PET-3807 happened: the two backend gates that enforce it, and the office
// Documents card, which has to mark these rows as owed rather than leaving them
// reading "Optional" with no visible way to supply them.
export const BUSINESS_PROOF_DOCUMENT_SLUGS = [SUNBIZ_FILING.slug, SAFER_SNAPSHOT.slug] as const

const VOIDED_CHECK: OnboardingRequirementDefinition = {
  slug: 'voided_check',
  name: 'Voided check',
  description: 'Voided check or bank letter confirming the payment account.',
  isRequired: false,
  source: 'applicant_upload',
  sortOrder: 90,
}

// Either document satisfies the group — a state exemption certificate or an
// actual policy. Optional as a group on the OWNER-OPERATOR flow: Petrafi
// collects whichever the trucker has but does not gate onboarding on it. The
// carrier flow spreads these into a required group — see below.
const WORKERS_COMP_REQUIREMENTS: OnboardingRequirementDefinition[] = [
  {
    slug: 'workers_comp_certificate',
    name: "Workers' comp certificate",
    description: "Workers' compensation insurance certificate.",
    isRequired: false,
    oneOfGroup: 'workers_comp',
    source: 'applicant_upload',
    sortOrder: 100,
  },
  {
    slug: 'workers_comp_exemption',
    name: "Workers' comp exemption",
    description: "State-issued workers' compensation exemption certificate.",
    isRequired: false,
    oneOfGroup: 'workers_comp',
    source: 'applicant_upload',
    sortOrder: 110,
  },
]

// Required as a GROUP on the carrier flow, on both insurance paths: a motor
// carrier has employees, so it owes either real coverage or the state's
// exemption. Either row satisfies it — `requiredRequirementUnits` collapses a
// `oneOfGroup` into one unit — and BOTH carry `isRequired` because the wizard
// splits Required/Additional before it groups: flagging only one would tear the
// pair across the two sections and read as two separate obligations rather than
// a choice.
const CARRIER_WORKERS_COMP_REQUIREMENTS: OnboardingRequirementDefinition[] =
  WORKERS_COMP_REQUIREMENTS.map((requirement) => ({ ...requirement, isRequired: true }))

// Front and back of the licence go in one slot as two files rather than two
// slots: `document_files` already carries N files per document, and splitting
// would have orphaned every existing single-slot CDL upload.
// Required on the Emblem path only: we underwrite the vehicle, so we need to see
// it. On `carrier_onboarding` these rows are `scope: 'truck'` and hang off one
// declared unit each (PET-3167). The owner-operator flow still holds every
// truck's document as separate files in one slot — `maxFiles` is deliberately
// unset there. Giving that flow per-unit scopes too is PET-617.
const TRUCK_REGISTRATION: OnboardingRequirementDefinition = {
  slug: 'truck_registration',
  name: 'Truck registration',
  description: 'Vehicle registration for each truck being insured.',
  isRequired: true,
  conditionKey: 'needs_insurance',
  source: 'applicant_upload',
  sortOrder: 40,
}

const DOT_INSPECTION: OnboardingRequirementDefinition = {
  slug: 'dot_inspection',
  name: 'DOT inspection',
  description: 'Most recent annual DOT inspection report for each truck.',
  isRequired: true,
  conditionKey: 'needs_insurance',
  source: 'applicant_upload',
  sortOrder: 50,
}

const CDL_REQUIREMENT: OnboardingRequirementDefinition = {
  slug: 'cdl',
  name: 'CDL (front + back)',
  description: 'Commercial Driver License — upload both the front and the back.',
  isRequired: true,
  source: 'applicant_upload',
  sortOrder: 30,
  maxFiles: 2,
}

const MEDICAL_CARD_REQUIREMENT: OnboardingRequirementDefinition = {
  slug: 'medical_card',
  name: 'Medical card',
  description: 'DOT medical examiner certificate.',
  isRequired: true,
  conditionKey: 'age_65_plus',
  source: 'applicant_upload',
  sortOrder: 60,
}

// Not part of the Petrafi matrix, but these rows must exist: each is fetched by
// slug at runtime (`findRequirementBySlug`) to hang a generated artifact off,
// and a missing row throws rather than degrading. See
// services/insurance/signatureArchive.ts and services/insurance/index.ts.
const DRIVER_GENERATED_REQUIREMENTS: OnboardingRequirementDefinition[] = [
  {
    slug: SIGNED_AGREEMENT_SLUG,
    name: 'Signed agreement',
    description: 'Countersigned owner-operator agreement archived from the signing flow.',
    isRequired: false,
    source: 'signing_flow',
    sortOrder: 200,
  },
  {
    slug: 'broker_mc',
    name: 'Broker MC authority packet',
    description: 'System-generated cover PDF bundling company details and approved documents.',
    isRequired: false,
    source: 'system_generated',
    sortOrder: 210,
  },
  {
    slug: 'driver_docs',
    name: 'Driver document packet',
    description: "System-generated bundle of the driver's approved uploads.",
    isRequired: false,
    source: 'system_generated',
    sortOrder: 220,
  },
]

// Owner-operator. The insurance path decides almost the whole set: a trucker who
// already carries insurance only owes us proof of it plus a W-9, while one we
// underwrite through Emblem owes the full vehicle-and-licence packet.
const STANDARD_DRIVER_ONBOARDING_REQUIREMENTS: OnboardingRequirementDefinition[] = [
  // One COI per trucker, owed on BOTH paths — a certificate on file is the bare
  // minimum to dispatch a truck, however the coverage was placed. The Emblem
  // clause only opens once the quote is accepted: before that no certificate
  // exists, so asking for it would block an applicant who cannot comply.
  {
    slug: 'insurance_certificate',
    name: 'Insurance COI',
    description: 'Certificate of insurance for the policy covering the trucker.',
    isRequired: true,
    conditionKey: 'has_own|needs_insurance&emblem_quote_accepted',
    source: 'applicant_upload',
    sortOrder: 10,
  },
  {
    slug: 'w9',
    name: 'W-9',
    description: 'Signed IRS Form W-9 identifying the payee.',
    isRequired: true,
    source: 'applicant_upload',
    sortOrder: 20,
  },
  { ...CDL_REQUIREMENT, conditionKey: 'needs_insurance' },
  TRUCK_REGISTRATION,
  DOT_INSPECTION,
  // An owner-operator who already carries insurance owes us no medical card at
  // all, so this is gated on the Emblem path as well as the age threshold.
  { ...MEDICAL_CARD_REQUIREMENT, conditionKey: 'needs_insurance&age_65_plus' },
  SUNBIZ_FILING,
  SAFER_SNAPSHOT,
  VOIDED_CHECK,
  ...WORKERS_COMP_REQUIREMENTS,
  ...DRIVER_GENERATED_REQUIREMENTS,
]

// Carrier company. The only flow with per-unit scopes (PET-3167): the applicant
// declares N truck-and-driver combos on the wizard's fleet step and each combo
// carries its own documents, because Emblem cannot underwrite a truck whose
// registration it has never seen.
//
// `owner_drives` and `owner_age_65_plus` no longer appear here. The owner is not
// a special case any more — when they tick "I will drive myself" their driver leg
// becomes the first pair card, so their CDL and medical card are the ordinary
// per-driver rows below, and their date of birth lives on that card rather than
// on the satellite.
//
// The medical card is therefore gated per DRIVER on `age_65_plus`, the same as
// the owner-operator flow. This briefly read `needs_insurance` alone, on the
// grounds that the age atom had no per-driver date of birth to read — true of
// the satellite's single `ownerDateOfBirth`, but not once the pair cards landed
// one date per card (`CARR-18`) and the wizard began requiring it on the Emblem
// path. The condition simply never caught up, so every carrier driver was asked
// for a medical card regardless of age.
const CARRIER_ONBOARDING_REQUIREMENTS: OnboardingRequirementDefinition[] = [
  // Per TRUCK, on both paths. Commercial auto liability insures the vehicle, and
  // Fleet keeps exactly one COI slot per pair on the truck (`FLT-33`) — collecting
  // it anywhere else is what left promoted trucks sitting at `pending_docs` with
  // the certificate that satisfies them filed one entity away. The condition is
  // character-for-character the owner-operator flow's, so the two catalogs agree:
  // the Emblem clause only opens once the quote is accepted, because before that
  // no certificate exists and asking for it would block an applicant who cannot
  // comply.
  {
    slug: 'insurance_certificate',
    name: 'Insurance COI',
    description: 'Certificate of insurance covering this truck.',
    isRequired: true,
    conditionKey: 'has_own|needs_insurance&emblem_quote_accepted',
    scope: 'truck',
    source: 'applicant_upload',
    sortOrder: 10,
  },
  {
    slug: 'w9',
    name: 'W-9',
    description: 'Signed IRS Form W-9 identifying the carrier for payment.',
    isRequired: true,
    source: 'applicant_upload',
    sortOrder: 20,
  },
  { ...CDL_REQUIREMENT, conditionKey: 'needs_insurance', scope: 'driver' },
  // Owed on the Emblem path, where Emblem reviews them (`INS-54`); offered as
  // optional to a self-insured carrier, whose office reviews whatever it sends
  // (`DOC-45`). The owner-operator flow keeps hiding them on `has_own`.
  { ...TRUCK_REGISTRATION, scope: 'truck', optionalConditionKey: 'has_own' },
  { ...DOT_INSPECTION, scope: 'truck', optionalConditionKey: 'has_own' },
  // Same gate as the owner-operator flow's, now that each pair card carries its
  // own date of birth (`CARR-18`) and `withSubjectContext` overlays it per unit.
  // `age_65_plus` fails OPEN on an unknown date, so a card whose date of birth
  // is missing still owes the card rather than skipping it silently.
  { ...MEDICAL_CARD_REQUIREMENT, conditionKey: 'needs_insurance&age_65_plus', scope: 'driver' },
  SUNBIZ_FILING,
  SAFER_SNAPSHOT,
  VOIDED_CHECK,
  ...CARRIER_WORKERS_COMP_REQUIREMENTS,
  // Office-generated broker MC packet, produced after the insurance step is
  // done. Mirrors the driver flow's broker_mc requirement — bundles the cover
  // page + all approved applicant uploads into a single PDF.
  {
    slug: 'broker_mc',
    name: 'Broker MC authority packet',
    description: 'System-generated cover PDF bundling company details and approved documents.',
    isRequired: false,
    source: 'system_generated',
    sortOrder: 210,
  },
  // The signing flow's two artifacts (PET-3819), company scope, and the exact
  // counterparts of `DRIVER_GENERATED_REQUIREMENTS` above — an owner operator
  // and a motor carrier sign the SAME agreement against the same DocuSeal
  // template, so the carrier catalog owes the same two rows.
  //
  // `isRequired: false` on both, like every generated row: `buildReviewSlots`
  // filters the office checklist on `isApplicantUploadSource`, so marking them
  // required would gate nothing there while making
  // `assertRequiredDocumentsApproved` demand a document the applicant cannot
  // supply. What actually gates on them is `assertCarrierReadyForApproval`,
  // explicitly (`CARR-02`).
  //
  // The non-applicant `source` is also what keeps `DOC-17` holding: the token
  // endpoint refuses an upload against any requirement outside
  // `isApplicantUploadSource`, so an applicant cannot manufacture the agreement
  // that gates their own approval.
  {
    slug: SIGNED_AGREEMENT_SLUG,
    name: 'Signed agreement',
    description: 'Countersigned motor carrier agreement archived from the signing flow.',
    isRequired: false,
    source: 'signing_flow',
    sortOrder: 200,
  },
  {
    slug: 'carrier_docs',
    name: 'Motor carrier file',
    description: 'System-generated bundle of the broker MC packet and the countersigned agreement.',
    isRequired: false,
    source: 'system_generated',
    sortOrder: 220,
  },
]

// Placeholder while product finalizes the broker packet. Once confirmed
// (typical set: MC broker authority, W-9, bond/BOA, insurance certificate),
// extend this array and re-run `pnpm --filter @repo/db db:reseed-onboarding-requirements`.
const HEADCOUNT_ONBOARDING_REQUIREMENTS: OnboardingRequirementDefinition[] = [
  {
    slug: 'w9',
    name: 'W-9',
    description: 'Signed IRS Form W-9 identifying the broker for payment.',
    isRequired: true,
    source: 'applicant_upload',
    sortOrder: 10,
  },
]

export const ONBOARDING_REQUIREMENT_DEFINITIONS: Record<string, OnboardingRequirementDefinition[]> =
  {
    carrier_onboarding: CARRIER_ONBOARDING_REQUIREMENTS,
    standard_driver_onboarding: STANDARD_DRIVER_ONBOARDING_REQUIREMENTS,
    headcount_onboarding: HEADCOUNT_ONBOARDING_REQUIREMENTS,
  }

// Documents an office admin may pre-attach when sending an invitation. Shared by
// the invitation modals and the `/with-documents` endpoint so both agree on the
// slug set, and therefore on the multipart field names. System-generated and
// signing-flow documents are excluded — nobody uploads those. A flow absent
// here does not accept pre-attached documents at all.
//
// Owner-operator invites mirror the carrier invite's full applicant-upload set
// (COI, W-9, truck registration, DOT inspection, Sunbiz, SAFER, voided check,
// workers' comp, CDL, medical card): an owner-operator carries all the same
// company paperwork as a solo carrier, and offices routinely have those files
// on hand at invite time. The carrier-hired driver invite stays on the fleet
// driver catalog — the carrier owns the company documents on that path.
export const INVITATION_DOCUMENT_REQUIREMENTS = {
  standard_driver_onboarding: STANDARD_DRIVER_ONBOARDING_REQUIREMENTS.filter(
    (r) => r.source === 'applicant_upload',
  ),
  carrier_onboarding: CARRIER_ONBOARDING_REQUIREMENTS.filter(
    (r) => r.source === 'applicant_upload',
  ),
} satisfies Record<string, readonly DocumentRequirementDescriptor[]>

export type InvitationDocumentFlowKey = keyof typeof INVITATION_DOCUMENT_REQUIREMENTS

// The Spanish label of each catalog slug, for what the server writes to a
// Spanish-speaking driver (`OO-49`): a push, an email. Same wording as the office
// app's `documents:driver.*` keys. Keyed by slug rather than stored on the row,
// so a tenant's own row for a slug reads the same; a slug absent here falls
// back to the row's English name.
export const ONBOARDING_REQUIREMENT_NAMES_ES: Readonly<Record<string, string>> = {
  cdl: 'CDL (frente + reverso)',
  medical_card: 'Tarjeta médica',
  workers_comp_exemption: 'Exención de compensación laboral',
  workers_comp_certificate: 'Certificado de compensación laboral',
  insurance_certificate: 'COI de seguro',
  w9: 'W-9',
  sunbiz_filing: 'Registro Sunbiz',
  safer_snapshot: 'Informe SAFER',
  voided_check: 'Cheque anulado',
  truck_registration: 'Registro del camión',
  dot_inspection: 'Inspección DOT',
  signed_agreement: 'Acuerdo firmado',
  broker_mc: 'Paquete de autoridad MC del bróker',
  driver_docs: 'Paquete de documentos del propietario operador',
}

/** A requirement's name in `language`, falling back to the row's own (English) name. */
export const requirementNameIn = (
  requirement: { slug: string | null; name: string },
  language: 'en' | 'es',
): string =>
  language === 'es' && requirement.slug
    ? (ONBOARDING_REQUIREMENT_NAMES_ES[requirement.slug] ?? requirement.name)
    : requirement.name
